/** Modèle pur de l’aperçu au survol d’une tâche (liste dossier et liste Tâches). */
import { formatAppDate } from '../../lib/dateFormat';

export type TaskPreviewStatus = 'late' | 'open' | 'done';
export type TaskPreviewTone = 'high' | 'mid' | 'low';

export interface TaskPreviewPerson {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export interface TaskPreviewTag {
  id: string;
  name: string;
  color: string;
}

export interface TaskPreviewInput {
  title: string;
  description: string | null;
  isCompleted: boolean;
  /** due_date ou due_datetime. Vide = pas d’échéance. */
  dueIso: string | null;
  /** Heure déjà extraite. Vide si la tâche n’a qu’une date. */
  dueTime: string;
  deadlineIso: string | null;
  dossierTitle: string | null;
  /** Null si le cabinet n’affiche pas la priorité. */
  priority: { label: string; tone: TaskPreviewTone } | null;
  sectionName: string | null;
  tags: TaskPreviewTag[];
  people: TaskPreviewPerson[];
  commentCount: number;
  attachmentCount: number;
}

export interface TaskPreviewDue {
  day: string;
  month: string;
}

export interface TaskPreviewModel {
  status: TaskPreviewStatus;
  statusLabel: string;
  dossierTitle: string | null;
  title: string;
  /** Playfair sur le titre quand le dossier n’est pas affiché. */
  titleIsHeading: boolean;
  description: string | null;
  due: TaskPreviewDue | null;
  hour: string | null;
  relance: string | null;
  priority: { label: string; tone: TaskPreviewTone } | null;
  sectionName: string | null;
  tags: TaskPreviewTag[];
  people: TaskPreviewPerson[];
  commentCount: number;
  attachmentCount: number;
}

export function priorityTone(priority: number): TaskPreviewTone {
  if (priority === 1) return 'high';
  if (priority === 4) return 'low';
  return 'mid';
}

function localParts(iso: string): Date | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function formatRelance(iso: string): string | null {
  const d = localParts(iso);
  if (!d) return null;
  return formatAppDate(d);
}

/** Ligne « 2 commentaires · 1 pièce ». Null si les deux comptes sont à zéro. */
export function previewCountLine(comments: number, files: number): string | null {
  const parts: string[] = [];
  if (comments > 0) {
    parts.push(`${comments} commentaire${comments > 1 ? 's' : ''}`);
  }
  if (files > 0) {
    parts.push(`${files} pièce${files > 1 ? 's' : ''}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function buildTaskPreview(input: TaskPreviewInput, now: Date): TaskPreviewModel {
  const due = input.dueIso ? localParts(input.dueIso) : null;
  let late = false;
  if (due && !input.isCompleted) {
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
    late = dueDay < startToday;
  }
  const status: TaskPreviewStatus = input.isCompleted ? 'done' : late ? 'late' : 'open';
  const statusLabel = status === 'done' ? 'Terminée' : status === 'late' ? 'En retard' : 'À faire';
  const description = input.description?.trim() ? input.description.trim() : null;
  const hour = input.dueTime.trim() ? input.dueTime.trim() : null;
  const dossierTitle = input.dossierTitle?.trim() ? input.dossierTitle.trim() : null;
  const sectionName = input.sectionName?.trim() ? input.sectionName.trim() : null;

  return {
    status,
    statusLabel,
    dossierTitle,
    title: input.title,
    titleIsHeading: dossierTitle == null,
    description,
    due: due
      ? {
          day: String(due.getDate()),
          month: due.toLocaleDateString('fr-FR', { month: 'short' }),
        }
      : null,
    hour: due && hour ? hour : null,
    relance: input.deadlineIso ? formatRelance(input.deadlineIso) : null,
    priority: input.priority,
    sectionName,
    tags: input.tags,
    people: input.people.filter((p) => p.name.trim()),
    commentCount: input.commentCount,
    attachmentCount: input.attachmentCount,
  };
}

/** Placement validé sur la maquette : sous la ligne, au-dessus si ça sort de l’écran. */
export function placeTaskPreview(
  rect: { left: number; top: number; bottom: number },
  card: { width: number; height: number },
  viewport: { width: number; height: number },
): { left: number; top: number } {
  const margin = 8;
  let left = rect.left + 28;
  if (left + card.width > viewport.width - 12) left = viewport.width - card.width - 12;
  if (left < margin) left = margin;
  let top = rect.bottom + margin;
  if (top + card.height > viewport.height - margin) {
    top = Math.max(margin, rect.top - card.height - margin);
  }
  return { left, top };
}
